import type { PublicationState } from "~types/types";
import { Brand, MagazineBrand, PublishedState } from "~types/wardenclyffe";

export const DEFAULT_LOCALE = "en";
export const MOBILE_AND_ABOVE_IN_PX = 768;
export enum ENVIRONMENTS {
    "production" = "production",
    "staging" = "staging",
    "development" = "development",
    "local" = "local",
    "red" = "red",
    "blue" = "blue",
    "green" = "green",
    "purple" = "purple",
    "orange" = "orange",
    "yellow" = "yellow",
}

export enum SUPPORTED_THEMES {
    businessweek = "businessweek",
    opinionPage = "opinion_page",
    weekend = "weekend",
    fullBleed = "full_bleed",
}

export const BUSINESSWEEK_BRAND = "businessweek";
export const LAZY_LOAD_STARTING_ZONES = 3;
export const PUBLISHED_STATE: Record<string, PublicationState> = {
    PUBLISHED: PublishedState.Published,
    DRAFT: PublishedState.Draft,
};
export const MAGAZINE_URL_ROOT = "/magazine";
export const MARKETS_BRANDS = {
    MAGAZINE: "markets-magazine",
    CANONICAL: "markets",
};
export const BW_PURSUITS = "bw_pursuits";
export const LEFT_RAIL = "leftRail";
export const RIGHT_RAIL = "rightRail";

export const PAGE_TYPES = {
    home: "lineup-homepage",
    default: "lineup-sectionfront",
};

export const LOCALES = {
    japan: "ja",
    default: "en",
};

export const ANALYTICS_TEMPLATE_NAMES = {
    error: "new-lineup-error",
    default: "new-lineup",
};

export const PHOENIX_LIKE_PAGE_ID = "phoenix";

export const HOMEPAGE_PAGE_IDS = [
    "cojp-v2",
    "premium",
    "premium-africa",
    "premium-asia",
    "premium-canada",
    "premium-europe",
    "premium-middle-east",
    "premium-uk",
    "phoenix",
    "phoenix-americas",
    "phoenix-africa",
    "phoenix-asia",
    "phoenix-canada",
    "phoenix-europe",
    "phoenix-middle-east",
    "phoenix-uk",
];

export const DROPDOWN_LABEL = [
    "noLabel",
    "live",
    "analysis",
    "exclusive",
    "breaking",
    "breakingTime",
    "bigTake",
    "comingUp",
    "marketsWrap",
];

export const MAGAZINE_BRANDS: { [key: string]: string } = {
    businessweek: MagazineBrand.BusinessWeek,
    green: MagazineBrand.Green,
    markets: MagazineBrand.Markets,
};

export const TTL_CONSTANTS = {
    one_hour: 3600,
};

export const MODULES_WITHOUT_DEFAULT = ["ad"];
export const LINEUP_CONTENT_DEFAULT_COMPONENT_NAME = "LineupContentDefault";

export const FIVE_SECONDS_IN_MILLISECONDS = 5000;

export enum CONTENT_TYPE {
    "article" = "article",
    "audio" = "audio",
    "author_session" = "author_session",
    "breaking_news" = "breaking_news",
    "data_article" = "data_article",
    "default" = "default",
    "explainer" = "explainer",
    "feature" = "feature",
    "impact_scorecard" = "impact_scorecard",
    "interactive" = "interactive",
    "letter" = "letter",
    "liveblog" = "liveblog",
    "newsletter" = "newsletter",
    "newsletter_campaign" = "newsletter_campaign",
    "quicktake" = "quicktake",
    "slideshow" = "slideshow",
    "storythread" = "storythread",
    "video" = "video",
}

export const DEFAULT_VARIATION_FOR_OLD_MODULE_TYPES: {
    [key: string]: string;
} = {
    story_package: "4_up_images",
    hero: "businessweek_white",
    liveStream: "qt_video_well",
    grid: "imageless",
};

export const ZONES = ["full", "righty", "lefty", "switch"];

export const ZONES_WITH_COLUMNS = {
    switch: ["left-rail", "content", "right-rail"],
    righty: ["content", "right-rail"],
    lefty: ["left-rail", "content"],
    full: ["content"],
};

export const GRID_ZONES_WITH_COLUMNS = {
    righty: ["left-content-well", "grid-right-rail"],
    full: ["full-width-content-well"],
    switch: ["unsupported"],
    lefty: ["unsupported"],
};

export const MARKETS_WRAP_NICODE = "GLOBEWRP";

export const CUSTOM_STYLE_THEMES = new Set(["businessweek", "bw_pursuits"]);

export const MUST_CONSTRUCT_BASED_ON_CONTENT_TYPE = new Set([
    CONTENT_TYPE.interactive,
    CONTENT_TYPE.quicktake,
    CONTENT_TYPE.letter,
    CONTENT_TYPE.video,
    CONTENT_TYPE.article,
    CONTENT_TYPE.feature,
    CONTENT_TYPE.author_session,
    CONTENT_TYPE.explainer,
    "curatedLink",
]);

export const MAX_RELATED_ITEMS_FOR_SINGLE_STORY = 3;

export const CURATED_TRACKING_TYPE = "Curated";

export enum WardenclyffeUser {
    DEFAULT = "default",
    COJP = "cojp",
    PREVIEW = "preview",
}

export const RELATIVE_PATH_INITIATOR: {
    [key: string]: string;
} = {
    view: "/opinion",
    gadfly: "/opinion",
    default: "/news",
};

export const CONTENT_TYPE_TO_PATH: {
    [key: string]: string;
} = {
    [CONTENT_TYPE.article]: "articles",
    [CONTENT_TYPE.audio]: "audio",
    [CONTENT_TYPE.breaking_news]: "articles",
    [CONTENT_TYPE.data_article]: "data",
    [CONTENT_TYPE.explainer]: "explainers",
    [CONTENT_TYPE.feature]: "features",
    [CONTENT_TYPE.liveblog]: "live-blog",
    [CONTENT_TYPE.newsletter_campaign]: "newsletters",
    [CONTENT_TYPE.slideshow]: "photo-essays",
    [CONTENT_TYPE.storythread]: "storythreads",
    [CONTENT_TYPE.video]: "videos",
    [CONTENT_TYPE.default]: "articles",
    [CONTENT_TYPE.impact_scorecard]: "impact-scorecard",
};

// letters, numbers, underscores (_), dashes (-)
export const VALID_RESOURCE_ID_REGEX = /^(\w|-)+$/;

export const RX_EXTENSIONS = /^.*[./].*/;

export const TWITTER_HOST = "https://www.twitter.com";

export const AD_CODE_MAP: Record<string, string> = {
    "new-audio": "bloomberg/audio/home/",
    author: "bloomberg/author",
    businessweek: "bloomberg/businessweek/index",
    "bw-qa": "bloomberg/businessweek/index",
    "businessweek-archive": "bloomberg/businessweek/archive/index",
    "businessweek-v2": "bloomberg/businessweek/index",
    "businessweek-magazine": "bloomberg/businessweek",
    "cojp-v2": "japan/home",
    "cojp-v2-overseas": "japan/news/overseas/index",
    "cojp-v2-markets": "japan/markets/stocks/index",
    "cojp-v2-domestic": "japan/news/domestic/index",
    "cojp-v2-economy": "japan/news/economy/index",
    "cojp-v2-exclusive": "japan/news/exclusive/index",
    "cojp-v2-commentary": "japan/news/commentary/index",
    etfs: "markets/etfcenter",
    "fixed-income": "markets/fixedincome",
    "work-shift": "bloomberg/work-shift/index",
    "work-shift-work-in-progress":
        "bloomberg/work-shift/work-in-progress/index",
    "work-shift-modern-management":
        "bloomberg/work-shift/modern-management/index",
    "work-shift-future-you": "bloomberg/work-shift/future-you/index",
    "work-shift-technology-and-skills":
        "bloomberg/work-shift/technology-and-skills/index",
    industries: "bloomberg/industries/index",
    "industries-energy": "bloomberg/industries/energy/index",
    "industries-entertainment": "bloomberg/industries/entertainment/index",
    "industries-finance": "bloomberg/industries/finance/index",
    "industries-health": "bloomberg/industries/health/index",
    "industries-consumer": "bloomberg/industries/consumer/index",
    "industries-legal": "bloomberg/industries/legal/index",
    "industries-transportation": "bloomberg/industries/transportation/index",
    "industries-telecom": "bloomberg/industries/telecom/index",
    green: "bloomberg/green/index",
    "green-magazine": "bloomberg/green",
    "green-new-energy": "bloomberg/green/new-energy/index",
    "green-esg-investing": "bloomberg/green/esg-investing/index",
    "green-weather-science": "bloomberg/green/weather-science/index",
    "green-electric-vehicles": "bloomberg/green/electric-vehicles/index",
    "green-climate-politics": "bloomberg/green/climate-politics/index",
    "green-greener-living": "bloomberg/green/greener-living/index",
    "green-cleaner-tech": "bloomberg/green/cleaner-tech/index",
    crypto: "bloomberg/crypto/index",
    premium: "bloomberg/home/americas/index",
    "premium-uk": "bloomberg/home/uk/index",
    "premium-africa": "bloomberg/home/africa/index",
    "premium-asia": "bloomberg/home/asia/index",
    "premium-canada": "bloomberg/home/canada/index",
    "premium-europe": "bloomberg/home/europe/index",
    "premium-middle-east": "bloomberg/home/middle-east/index",
    phoenix: "bloomberg/home/americas/phoenix/index",
    "phoenix-americas": "bloomberg/home/americas/phoenix/index",
    "phoenix-uk": "bloomberg/home/uk/phoenix/index",
    "phoenix-africa": "bloomberg/home/africa/phoenix/index",
    "phoenix-asia": "bloomberg/home/asia/phoenix/index",
    "phoenix-canada": "bloomberg/home/canada/phoenix/index",
    "phoenix-europe": "bloomberg/home/europe/phoenix/index",
    "phoenix-middle-east": "bloomberg/home/middle-east/phoenix/index",
    markets: "bloomberg/markets/index",
    opinion: "bloomberg/opinion/index",
    politics: "bloomberg/politics/index",
    technology: "bloomberg/technology/index",
    "pursuits-vp": "bloomberg/pursuits/index",
    "economics-v2": "bloomberg/economics/index",
    "economics-indicators": "bloomberg/economics/indicators/index",
    "economics-central-banks": "bloomberg/economics/central-banks/index",
    "economics-jobs": "bloomberg/economics/jobs/index",
    "economics-trade": "bloomberg/economics/trade/index",
    "economics-tax-and-spend": "bloomberg/economics/tax-spend/index",
    "economics-inflation-and-prices":
        "bloomberg/economics/inflation-prices/index",
    travel: "bloomberg/pursuits/travel/index",
    "real-estate-and-home": "bloomberg/pursuits/real-estate/index",
    "pursuits-culture": "bloomberg/pursuits/culture-pursuits/index",
    cars: "bloomberg/pursuits/cars/index",
    style: "bloomberg/pursuits/style/index",
    living: "bloomberg/pursuits/food-drinks/index",
    "opinion-business": "bloomberg/opinion/business-opinion/index",
    "opinion-finance": "bloomberg/opinion/finance-opinion/index",
    "opinion-politics-and-policy": "bloomberg/opinion/politics-policy/index",
    "opinion-markets": "bloomberg/opinion/markets/index",
    "opinion-technology-and-ideas": "bloomberg/opinion/technology-ideas/index",
    "opinion-economics": "bloomberg/opinion/economics-opinion/index",
    "markets-magazine-v2": "bloomberg/markets/markets-magazine/index",
    "markets-magazine": "bloomberg/markets",
    "video-v2": "bloomberg/vod/index",
    "graphics-v2": "bloomberg/graphics/index",
    "live-events-v2": "bloomberg/live/events/index",
    "business-schools-v2": "bloomberg/businessweek/business-schools/index",
    "personal-finance-v2": "bloomberg/personal-finance/index",
    "small-business-v2": "bloomberg/small-business/index",
    "india-v2": "bloomberg/home/india/index",
    latest: "bloomberg/latest",
    "latest-record": "bloomberg/latest/record",
    search: "bloomberg/search",
};

export const PHOENIX_PREFIX = "phx-";

export const DEFAULT_NATIVE_WIDTH = 1200;

export type VIDEO_VIEWPORT_ASPECT_RATIO = "16:9" | "2:3" | "1:1";

export enum REALTIME_DATA_PROVIDER {
    TICKER = "TICKER",
}

export const STORY_BRANDS = [
    Brand.Markets,
    Brand.Economics,
    Brand.Technology,
    Brand.Politics,
    Brand.Crypto,
    Brand.Green,
];

export const STORY_TAGS = {
    AI: [{ id: "artificial-intelligence", type: "Topic" }],
};

export enum RESOURCE_TYPE {
    "article" = "article",
    "author" = "author",
    "breaking_news" = "breaking_news",
    "data_article" = "data_article",
    "feature" = "feature",
    "interactive" = "interactive",
    "link" = "link",
    "quicktake" = "quicktake",
    "slideshow" = "slideshow",
    "tracker" = "tracker",
    "video" = "video",
    "default" = "default",
}

export enum TRACKING_TYPE {
    "Story" = "Story",
    "Author" = "Author",
    "Dataarticle" = "Dataarticle",
    "Feature" = "Feature",
    "Interactive" = "Interactive",
    "Curated" = "Curated",
    "Quicktake" = "Quicktake",
    "Slideshow" = "Slideshow",
    "Tracker" = "Tracker",
    "Video" = "Video",
}

export const TRACKING_TYPES = {
    [RESOURCE_TYPE.article]: TRACKING_TYPE.Story,
    [RESOURCE_TYPE.author]: TRACKING_TYPE.Author,
    [RESOURCE_TYPE.breaking_news]: TRACKING_TYPE.Story,
    [RESOURCE_TYPE.data_article]: TRACKING_TYPE.Dataarticle,
    [RESOURCE_TYPE.feature]: TRACKING_TYPE.Feature,
    [RESOURCE_TYPE.interactive]: TRACKING_TYPE.Interactive,
    [RESOURCE_TYPE.link]: TRACKING_TYPE.Curated,
    [RESOURCE_TYPE.quicktake]: TRACKING_TYPE.Quicktake,
    [RESOURCE_TYPE.slideshow]: TRACKING_TYPE.Slideshow,
    [RESOURCE_TYPE.tracker]: TRACKING_TYPE.Tracker,
    [RESOURCE_TYPE.video]: TRACKING_TYPE.Video,
    [RESOURCE_TYPE.default]: TRACKING_TYPE.Story,
};

export const DEFAULT_STORIES_QUERY_LIMIT = 10;
export const DEFAULT_PAGE_NUMBER = 1;
